import { cx } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { USER_AREA_BASE_PATH } from '@pm2/api';
import { pm2SitemapStore } from '@pm2/sitemap-utils';
import { useAppContext } from '@provider/app-context';
import { useLocation } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { HeartIcon, ResultsIcon, CoursesIcon, PortalIcon, TeamsIcon, ReadOutLoudIcon, FontIcon, EkgIcon } from '@ui/kit/icons';
import { Routes } from '../const.routes.js';
import { isNotificationConsideredRecent } from '../features/notifications/util.is-considered-recent.js';
import { getStatusForProductQuery } from '../features/status/api.get-status.js';
import { leftNavCls } from './_left-nav.style.js';
import { Group } from './atom.group.js';
import { InternalLink } from './atom.link.js';
import { User } from './atom.user.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { selectIsFavouritesEnabled } from '../features/favorites/util.is-enabled.js';
import { useGetNotificationsQuery } from '../features/notifications/api.get-notifications.query.js';

const userAreaNavSelector = `nav[data-userarea-nav]`;
function UserAreaLeftNav(props) {
  const location = useLocation();
  const user = useUserIdentityState(state => state.user);
  const isFavouritesEnabled = pm2SitemapStore.useSelector(selectIsFavouritesEnabled);
  return /*#__PURE__*/jsxs("nav", {
    className: cx(leftNavCls, props.className, location.pathname !== "/" && "atContent"),
    children: [/*#__PURE__*/jsx(User, {}), /*#__PURE__*/jsxs("ul", {
      children: [/*#__PURE__*/jsxs(Group, {
        children: [isFavouritesEnabled && /*#__PURE__*/jsx(FavouritesLink, {}), /*#__PURE__*/jsx(EntriesLink, {}), /*#__PURE__*/jsx(CoursesLink, {}), /*#__PURE__*/jsx(PortalsLink, {}), user?.role === "teacher" && /*#__PURE__*/jsx(TeamsLink, {})]
      }), /*#__PURE__*/jsxs(Group, {
        children: [/*#__PURE__*/jsx(SpeechLink, {}), /*#__PURE__*/jsx(FontLink, {}), /*#__PURE__*/jsx(StatusLink, {})]
      })]
    })]
  });
}
function PortalsLink() {
  const location = useLocation();
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Fagportaler",
    icon: /*#__PURE__*/jsx(PortalIcon, {
      height: "2.2rem"
    }),
    target: Routes.PORTALS,
    active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.PORTALS}`)
  });
}
function FavouritesLink() {
  const location = useLocation();
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Favoritter",
    icon: /*#__PURE__*/jsx(HeartIcon, {
      height: "1.8rem"
    }),
    target: Routes.FAVOURTIES,
    active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.FAVOURTIES}`)
  });
}
function CoursesLink() {
  const location = useLocation();
  const notifications = useGetNotificationsQuery({
    enabled: false
  });
  const notificationCount = notifications.data?.filter(it => it.type === "share.course" && isNotificationConsideredRecent(it)).length;
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Mine forløb",
    count: notificationCount,
    icon: /*#__PURE__*/jsx(CoursesIcon, {
      height: "2.2rem"
    }),
    target: Routes.COURSES,
    active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.COURSES}`)
  });
}
function EntriesLink() {
  const location = useLocation();
  const notifications = useGetNotificationsQuery({
    enabled: false
  });
  const notificationCount = notifications.data?.filter(it => it.type === "share.componentEntry" && isNotificationConsideredRecent(it)).length;
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Besvarelser",
    count: notificationCount,
    icon: /*#__PURE__*/jsx(ResultsIcon, {
      height: "2.2rem"
    }),
    target: Routes.ENTRIES,
    active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.ENTRIES}`)
  });
}
function TeamsLink() {
  const location = useLocation();
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(InternalLink, {
      label: "Mine hold",
      icon: /*#__PURE__*/jsx(TeamsIcon, {
        height: "2.2rem"
      }),
      target: Routes.TEAMS,
      active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.TEAMS}`)
    })
  });
}
function SpeechLink() {
  const location = useLocation();
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(InternalLink, {
      label: "Indstil oplæsning",
      icon: /*#__PURE__*/jsx(ReadOutLoudIcon, {
        height: "2.2rem"
      }),
      target: Routes.SPEECH,
      active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.SPEECH}`)
    })
  });
}
function FontLink() {
  const location = useLocation();
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(InternalLink, {
      label: "Indstil skrifttype",
      icon: /*#__PURE__*/jsx(FontIcon, {
        height: "2.2rem"
      }),
      target: Routes.FONT,
      active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.FONT}`)
    })
  });
}
function StatusLink() {
  const productId = useAppContext(state => state.product?.isbn);
  const location = useLocation();
  const status = useQuery({
    ...getStatusForProductQuery(productId),
    enabled: false
  });
  const statusCount = status.data?.length;
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Teknisk support",
    count: statusCount,
    icon: /*#__PURE__*/jsx(EkgIcon, {
      height: "1.6rem"
    }),
    target: Routes.SUPPORT,
    active: location.pathname.includes(`${USER_AREA_BASE_PATH}/${Routes.SUPPORT}`)
  });
}

export { UserAreaLeftNav, userAreaNavSelector };
//# sourceMappingURL=_left-nav.js.map
